import React from "react";
import { Trash2, X } from "lucide-react";

/**
 * Sticky bottom bar that appears when at least one record is selected.
 *
 * Responsibilities:
 *   • Show the current selection count (never on if 0)
 *   • Provide a "Clear selection" affordance
 *   • Provide the primary "Delete selected" affordance
 *
 * Layout: full-width on mobile, floating pill on ≥sm. Sits at z-40 above
 * the InstallerShell footer but below the Notification portal (z-70) and
 * confirmation modal (z-80).
 */
export default function BulkActionBar({
    count,
    labelSingular,
    labelPlural,
    onClear,
    onDelete,
    disabled = false,
    secondaryAction = null,   // {label, onClick, testid, tone?: "danger"|"neutral"}
    testidPrefix = "bulk",
}) {
    if (!count) return null;
    const label = count === 1 ? labelSingular : labelPlural;
    return (
        <div
            className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 z-40 px-3 pb-3 sm:pb-0 pointer-events-none"
            data-testid={`${testidPrefix}-action-bar`}
        >
            <div
                className="pointer-events-auto flex items-center gap-3 bg-[#0B1220] text-white shadow-2xl rounded-xl px-4 py-3 sm:min-w-[380px]"
                style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
                <div className="flex-1 min-w-0">
                    <div
                        className="text-sm font-medium truncate"
                        data-testid={`${testidPrefix}-count`}
                    >
                        {count} {label} selected
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md text-xs text-white/85 hover:bg-white/10 transition-colors"
                    data-testid={`${testidPrefix}-clear`}
                    aria-label="Clear selection"
                    disabled={disabled}
                >
                    <X size={13} strokeWidth={1.75} />
                    <span className="hidden sm:inline">Clear</span>
                </button>
                {secondaryAction && (
                    <button
                        type="button"
                        onClick={secondaryAction.onClick}
                        disabled={disabled}
                        className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                            secondaryAction.tone === "danger"
                                ? "bg-[#7F1D1D] hover:bg-[#5F1010] text-white"
                                : "bg-white/10 hover:bg-white/20 text-white"
                        }`}
                        data-testid={secondaryAction.testid || `${testidPrefix}-secondary`}
                    >
                        {secondaryAction.label}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onDelete}
                    disabled={disabled}
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-xs font-medium bg-[#DA3535] hover:bg-[#B92E2E] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    data-testid={`${testidPrefix}-delete`}
                >
                    <Trash2 size={13} strokeWidth={1.75} />
                    Delete selected
                </button>
            </div>
        </div>
    );
}
